import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  AlertComponent,
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  ModalBodyComponent,
  ModalComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalTitleDirective,
  PageItemDirective,
  PageLinkDirective,
  PaginationComponent,
  RowComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { LegalAuditService } from 'app';
import { LegalAcceptanceAdminRecord, LegalAuditFilters, LegalAuditStats, Logger } from 'shared';

/**
 * Admin view of the legal acceptance history.
 *
 * Lives in libs/credo and is routed from each app's admin.routes.ts rather than being
 * copied per app (the pattern UserTreePageComponent already uses) — the data and the
 * columns are identical across brands.
 *
 * Read-only: the underlying records are immutable server-side, so there is deliberately
 * no edit or delete affordance to build.
 */
@Component({
  selector: 'app-legal-acceptances-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AlertComponent,
    BadgeComponent,
    ButtonDirective,
    CardComponent,
    CardBodyComponent,
    CardHeaderComponent,
    ColComponent,
    ContainerComponent,
    FormControlDirective,
    ModalComponent,
    ModalHeaderComponent,
    ModalTitleDirective,
    ModalBodyComponent,
    ModalFooterComponent,
    PaginationComponent,
    PageItemDirective,
    PageLinkDirective,
    RowComponent,
    SpinnerComponent,
    TableDirective
  ],
  templateUrl: './legal-acceptances-page.component.html',
  styleUrls: ['./legal-acceptances-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LegalAcceptancesPageComponent {
  private legalAuditService = inject(LegalAuditService);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('LegalAcceptancesPageComponent');

  protected loading = signal(true);
  protected loadError = signal(false);
  protected records = signal<LegalAcceptanceAdminRecord[]>([]);
  protected stats = signal<LegalAuditStats | null>(null);
  protected pagination = signal({ page: 1, per_page: 50, total: 0, total_pages: 0 });

  protected detail = signal<LegalAcceptanceAdminRecord | null>(null);
  protected detailOpen = signal(false);

  protected filters: LegalAuditFilters = { page: 1, per_page: 50 };

  constructor() {
    this.loadStats();
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadError.set(false);

    this.legalAuditService.getAcceptances(this.filters).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => {
        this.records.set(response.acceptances);
        this.pagination.set(response.pagination);
        this.loading.set(false);
      },
      error: error => {
        this.logger.error('Failed to load legal acceptances', error);
        this.loadError.set(true);
        this.loading.set(false);
      }
    });
  }

  private loadStats(): void {
    this.legalAuditService.getStats().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: stats => this.stats.set(stats),
      error: error => this.logger.error('Failed to load legal stats', error)
    });
  }

  protected applyFilters(): void {
    this.filters = { ...this.filters, page: 1 };
    this.load();
  }

  protected resetFilters(): void {
    this.filters = { page: 1, per_page: 50 };
    this.load();
  }

  protected goToPage(page: number): void {
    if (page < 1 || page > this.pagination().total_pages) {
      return;
    }
    this.filters = { ...this.filters, page };
    this.load();
  }

  protected pages(): number[] {
    const { page, total_pages } = this.pagination();
    // Window of at most 7 around the current page — the full list can run to hundreds.
    const start = Math.max(1, Math.min(page - 3, total_pages - 6));
    const end = Math.min(total_pages, start + 6);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
  }

  /** The single-record endpoint returns the fields the list omits (raw UA, geoip). */
  protected openDetail(record: LegalAcceptanceAdminRecord): void {
    this.detail.set(record);
    this.detailOpen.set(true);

    this.legalAuditService.getAcceptance(record.id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => this.detail.set(response.acceptance),
      error: error => this.logger.error('Failed to load acceptance detail', error)
    });
  }

  protected closeDetail(): void {
    this.detailOpen.set(false);
    this.detail.set(null);
  }

  protected onDetailVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeDetail();
    }
  }
}
